import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const SuspiciousBankMessageScenario = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedAnswers, setSelectedAnswers] = useState({})

  const handleAnswer = (stepNum, answer) => {
    setSelectedAnswers({ ...selectedAnswers, [stepNum]: answer })
  }

  // Calculate decision quality score
  const getDecisionQuality = () => {
    const correctAnswers = {
      4: 'C',  // Urgency is a warning sign
      5: 'B',  // Strange link matters
      6: 'D',  // Don't log in through suspicious link
      7: 'C',  // Don't share verification code
      8: 'C',  // Don't follow social pressure
      9: 'B',  // Verify through official app
      10: 'B'  // Final safe decision
    }
    
    let goodDecisions = 0
    Object.keys(correctAnswers).forEach(step => {
      if (selectedAnswers[step] === correctAnswers[step]) {
        goodDecisions++
      }
    })
    
    return goodDecisions
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-red-100 mb-1">Interaktívny scenár</div>
                <h1 className="text-3xl font-bold">🚨 Podozrivá správa z banky</h1>
              </div>
              {currentStep === 1 ? (
                <button
                  onClick={() => navigate('/scenarios')}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Späť na ročníky
                </button>
              ) : (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Späť
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2">
              {[...Array(10)].map((_, idx) => (
                <div
                  key={idx + 1}
                  className={`flex-1 h-2 rounded-full transition-all ${
                    idx + 1 <= currentStep ? 'bg-red-500' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-2 text-center">
              Krok {currentStep} z 10
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">

          {/* INTRO SLIDE 1: What this story is about */}
          {currentStep === 1 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">📱</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  O čo pôjde v tomto príbehu?
                </h2>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto">
                <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">⚠️</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Správa, ktorá vyzerá dôležite
                      </p>
                      <p className="text-gray-600 text-sm">
                        Niekedy príde správa, ktorá tvári, že je z banky. Môže vyzerať presvedčivo, ale nemusí byť pravá
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">👧</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Tvoja úloha
                      </p>
                      <p className="text-gray-600 text-sm">
                        Pomôžeš Sáre rozpoznať riziko a rozhodnúť sa bezpečne
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 border-l-4 border-orange-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">💡</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Čo sa naučíš
                      </p>
                      <p className="text-gray-600 text-sm">
                        Zistíš, ako rozpoznať podozrivé správy a ako konať bezpečne
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-8 py-4 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition text-lg shadow-lg hover:shadow-xl"
                >
                  Začať príbeh →
                </button>
              </div>
            </div>
          )}

          {/* INTRO SLIDE 2: What is phishing */}
          {currentStep === 2 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">🎣</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Čo je phishing?
                </h2>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto">
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🎭</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Podvodník sa tvári ako banka
                      </p>
                      <p className="text-gray-600 text-sm">
                        Phishing je podvod, kde niekto predstiera, že je dôveryhodná inštitúcia (napríklad banka)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🔑</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Cieľ: Získať citlivé údaje
                      </p>
                      <p className="text-gray-600 text-sm">
                        Podvodník chce získať heslá, overacie kódy alebo iné citlivé údaje
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 border-l-4 border-purple-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">👀</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Správa môže vyzerať presvedčivo
                      </p>
                      <p className="text-gray-600 text-sm">
                        Podvodná správa môže vyzerať ako skutočná, ale nemusí byť pravá
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-8 py-4 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition text-lg shadow-lg hover:shadow-xl"
                >
                  Pokračovať →
                </button>
              </div>
            </div>
          )}

          {/* INTRO SLIDE 3: What to watch out for */}
          {currentStep === 3 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">⚠️</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Na čo si dávať pozor?
                </h2>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto">
                <div className="bg-orange-50 border-l-4 border-orange-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🔗</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Podozrivé odkazy
                      </p>
                      <p className="text-gray-600 text-sm">
                        Link, ktorý vyzerá trochu inak ako obvykle, môže byť podvod
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">⏱️</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Naliehavý tlak
                      </p>
                      <p className="text-gray-600 text-sm">
                        "Urob to hneď, inak ti zablokujeme účet!" je varovný signál
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🔐</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Žiadosti o heslá alebo kódy
                      </p>
                      <p className="text-gray-600 text-sm">
                        Banka ťa nikdy nepožiada, aby si poslal heslo alebo overací kód cez správu
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">✅</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Pri pochybnostiach overiť
                      </p>
                      <p className="text-gray-600 text-sm">
                        Ak si neistý, overiť cez oficiálnu aplikáciu, webstránku, telefónnu linku alebo dôveryhodnú dospelú osobu
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => setCurrentStep(4)}
                  className="px-8 py-4 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition text-lg shadow-lg hover:shadow-xl"
                >
                  Začať rozhodovať →
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: Urgent SMS */}
          {currentStep === 4 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">📱</div>
                  <h2 className="text-2xl font-bold text-gray-900">Naliehavá SMS správa</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border-2 border-red-200">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Sára dostane SMS:
                  </p>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="text-sm font-mono text-gray-800">
                      <strong>BANKA:</strong> URGENTNÉ! Váš účet bude zablokovaný do 2 hodín. Kliknite na link a overte totožnosť: http://banka-overenie.xyz/login
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo by si poradil Sáre ako prvé?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(4, 'A')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Klikni hneď na link, inak ti zablokujú účet</div>
                  <div className="text-sm text-gray-600">Urgentné = musíš konať okamžite</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(4, 'B')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Banka ti píše urgentne, musí to byť dôležité</div>
                  <div className="text-sm text-gray-600">Urgentné správy sú vždy pravé</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(4, 'C')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Naliehavosť je často varovný signál. Najprv over, či je správa pravá</div>
                  <div className="text-sm text-gray-600">Tlak na rýchle konanie môže byť trik</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(4, 'D')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Ignoruj správu úplne, určite to nie je dôležité</div>
                  <div className="text-sm text-gray-600">Všetky SMS sú spam</div>
                </button>
              </div>

              {selectedAnswers[4] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[4] === 'C' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[4] === 'C' ? '✅ Správne!' : '⚠️ Pozor!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[4] === 'A' ? 'Naliehavosť sama o sebe nedokazuje, že správa je pravá. Tlak na rýchle konanie je často trik podvodníkov.' :
                      selectedAnswers[4] === 'B' ? 'Urgentné správy nemusia byť pravé. Podvodníci používajú naliehavosť, aby ťa donútili konať rýchlo bez premyslenia.' :
                      selectedAnswers[4] === 'C' ? 'Presne tak! Naliehavosť je často varovný signál. Podvodníci tlačia na rýchle konanie, aby si nepremýšľal. Najprv over, či je správa pravá.' :
                      'Aj keď väčšina podozrivých správ je spam, niektoré môžu byť nebezpečné. Lepšie je overiť cez oficiálnu aplikáciu alebo kontakt.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[4] === 'A' ? 'Podvodníci používajú naliehavosť ("do 2 hodín"), aby ťa donútili konať rýchlo bez premyslenia. Naliehavosť sama o sebe nie je dôkaz, že správa je pravá.' :
                      selectedAnswers[4] === 'B' ? 'Urgentné správy môžu byť podvod. Podvodníci vytvárajú falošný pocit naliehavosti, aby si klikol na link bez overenia. Banka by ťa kontaktovala aj iným spôsobom.' :
                      selectedAnswers[4] === 'C' ? 'Naliehavosť je bežný trik podvodníkov. Tlačia na okamžité konanie, aby si nepremýšľal a neskontroloval, či je správa pravá. Bezpečnejšie je najprv overiť cez oficiálnu aplikáciu alebo kontakt.' :
                      'Ignorovať všetky správy nie je múdre – niektoré môžu byť legitímne. Lepšie je overiť cez oficiálnu aplikáciu alebo telefónnu linku banky.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 5: Strange link */}
          {currentStep === 5 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🔗</div>
                  <h2 className="text-2xl font-bold text-gray-900">Podozrivý link</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Sára si všimne, že link v správe vyzerá trochu inak ako normálna adresa jej banky.
                  </p>
                  <p className="text-gray-700 mb-2">
                    Normálna adresa: <code className="bg-white px-2 py-1 rounded text-sm">www.banka.sk</code>
                  </p>
                  <p className="text-gray-700">
                    Link v SMS: <code className="bg-red-100 px-2 py-1 rounded text-sm text-red-700">banka-overenie.xyz</code>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Ako by si to vysvetlil Sáre?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(5, 'A')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Malý rozdiel v linku je normálny, nemusíš sa tým trápiť</div>
                  <div className="text-sm text-gray-600">Adresa je skoro rovnaká</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(5, 'B')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Aj malý rozdiel v adrese môže znamenať, že ide o podvod</div>
                  <div className="text-sm text-gray-600">Podvodníci používajú podobné adresy</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(5, 'C')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Banka používa rôzne adresy, je to normálne</div>
                  <div className="text-sm text-gray-600">Každá funkcia má inú adresu</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(5, 'D')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Všetky .xyz adresy sú bezpečné</div>
                  <div className="text-sm text-gray-600">Koncovka adresy nezáleží</div>
                </button>
              </div>

              {selectedAnswers[5] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[5] === 'B' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[5] === 'B' ? '✅ Výborne!' : '⚠️ Nie celkom!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[5] === 'A' ? 'Malý rozdiel v adrese môže byť veľký problém. Podvodníci vytvárajú podobné adresy, aby vyzerali dôveryhodne.' :
                      selectedAnswers[5] === 'B' ? 'Presne tak! Podvodníci používajú adresy, ktoré vyzerajú podobne ako oficiálna adresa banky (napr. banka-overenie.xyz namiesto banka.sk). Aj malý rozdiel môže znamenať podvod.' :
                      selectedAnswers[5] === 'C' ? 'Banka používa jednu hlavnú adresu. Ak vidíš úplne inú adresu, je to varovný signál.' :
                      'Koncovka adresy záleží. Podvodníci často používajú nezvyčajné koncovky (.xyz, .top, .click), ktoré nevyzerajú ako oficiálne adresy bánk.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[5] === 'A' ? 'Podvodníci vytvárajú adresy, ktoré vyzerajú podobne ako oficiálna adresa (napr. banka-overenie.xyz namiesto banka.sk). Malý rozdiel môže znamenať, že ide o podvodnú stránku.' :
                      selectedAnswers[5] === 'B' ? 'Podvodníci používajú adresy, ktoré vyzerajú podobne ako oficiálna adresa banky, aby oklamali ľudí. Aj malý rozdiel (iná koncovka, extra slovo) môže znamenať podvod. Vždy over oficiálnu adresu cez aplikáciu alebo webstránku.' :
                      selectedAnswers[5] === 'C' ? 'Banka používa jednu hlavnú adresu (napr. banka.sk). Ak vidíš úplne inú adresu, je to varovný signál. Podvodníci vytvárajú rôzne adresy, aby vyzerali legitímne.' :
                      'Koncovka adresy záleží. Podvodníci často používajú nezvyčajné koncovky (.xyz, .top, .click), pretože oficiálne adresy bánk majú stabilné koncovky (.sk, .com). To je varovný signál.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 6: Login request */}
          {currentStep === 6 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🔐</div>
                  <h2 className="text-2xl font-bold text-gray-900">Žiadosť o prihlásenie</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Sára klikne na link a objaví sa stránka, ktorá žiada prihlasovacie údaje (meno a heslo).
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Stránka vyzerá podobne ako oficiálna bankovaná stránka.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo by mala Sára urobiť?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(6, 'A')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Prihlás sa normálne, stránka vyzerá ako banka</div>
                  <div className="text-sm text-gray-600">Vyzerá to oficiálne</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(6, 'B')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Ak stránka žiada heslo, musí byť v poriadku</div>
                  <div className="text-sm text-gray-600">Každá stránka banky žiada heslo</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(6, 'C')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Zadaj heslo rýchlo, kým účet nezablokujú</div>
                  <div className="text-sm text-gray-600">Urgentnosť je dôležitá</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(6, 'D')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) NEPRIHLASUJ SA cez podozrivý link. Prihlás sa cez oficiálnu aplikáciu alebo webstránku</div>
                  <div className="text-sm text-gray-600">Bezpečné kanály sú dôležité</div>
                </button>
              </div>

              {selectedAnswers[6] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[6] === 'D' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[6] === 'D' ? '✅ Správne!' : '⚠️ POZOR!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[6] === 'A' ? 'Podvodná stránka môže vyzerať ako oficiálna. NIKDY sa neprihlasuj cez link v podozrivej správe.' :
                      selectedAnswers[6] === 'B' ? 'Podvodné stránky tiež žiadajú heslo – to je celý cieľ podvodu. Ak zadáš heslo, podvodník ho získa.' :
                      selectedAnswers[6] === 'C' ? 'Urgentnosť je trik. Ak zadáš heslo cez podozrivú stránku, podvodník ho získa a môže ti vykradnúť účet.' :
                      'Presne tak! NIKDY sa neprihlasuj cez link v podozrivej správe. Vždy sa prihlás cez oficiálnu aplikáciu alebo webstránku, ktorú si zadal sám.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[6] === 'A' ? 'Podvodná stránka môže vyzerať presne ako oficiálna bankovaná stránka. Ak sa prihlasíš cez podozrivý link, podvodník získa tvoje heslo a môže ti vykradnúť účet. Vždy sa prihlás cez oficiálnu aplikáciu alebo webstránku.' :
                      selectedAnswers[6] === 'B' ? 'Podvodné stránky tiež žiadajú heslo – to je celý cieľ phishingu. Podvodník vytvorí falošnú stránku, ktorá vyzerá ako banka, a čaká, kým zadáš heslo. Potom ho získa a môže ti vykradnúť účet.' :
                      selectedAnswers[6] === 'C' ? 'Urgentnosť je trik. Ak zadáš heslo cez podozrivú stránku, podvodník ho okamžite získa a môže ti vykradnúť účet. Banka ťa nikdy nebude tlačiť, aby si sa prihlásil cez link v SMS.' :
                      'Prihlásenie cez podozrivý link je veľmi riskantné. Podvodník vytvorí falošnú stránku a čaká, kým zadáš heslo. Vždy sa prihlás cez oficiálnu aplikáciu alebo webstránku, ktorú si zadal sám (nie cez link v správe).'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 7: Verification code request */}
          {currentStep === 7 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🔢</div>
                  <h2 className="text-2xl font-bold text-gray-900">Žiadosť o overací kód</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Sára dostane SMS od banky s overacím kódom: <code className="bg-white px-2 py-1 rounded text-sm">123456</code>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Podozrivá stránka žiada: <em>"Zadajte overací kód, ktorý ste dostali v SMS."</em>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo by mala Sára urobiť?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(7, 'A')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Zadaj kód, inak ti zablokujú účet</div>
                  <div className="text-sm text-gray-600">Musíš to urobiť hneď</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(7, 'B')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Overací kód je len číslo, nie je to nebezpečné</div>
                  <div className="text-sm text-gray-600">Kód nie je citlivý údaj</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(7, 'C')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) NEZADÁVAJ overací kód do podozrivej stránky. Kód je citlivý údaj</div>
                  <div className="text-sm text-gray-600">Overacie kódy sú ako heslo</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(7, 'D')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Pošli overací kód kamarátke, nech ti poradí</div>
                  <div className="text-sm text-gray-600">Kamarátka ti pomôže</div>
                </button>
              </div>

              {selectedAnswers[7] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[7] === 'C' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[7] === 'C' ? '✅ Výborne!' : '⚠️ POZOR!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[7] === 'A' ? 'Overací kód je citlivý údaj. Ak ho zadáš do podozrivej stránky, podvodník môže potvrdiť transakcie alebo zmeny na tvojom účte.' :
                      selectedAnswers[7] === 'B' ? 'Overací kód NIE JE len číslo. Je to citlivý údaj, ktorý potvrdzuje transakcie. Nikdy ho nezadávaj do podozrivej stránky.' :
                      selectedAnswers[7] === 'C' ? 'Presne tak! Overací kód je citlivý údaj, ktorý potvrdzuje transakcie. Nikdy ho nezadávaj do podozrivej stránky ani nezdieľaj s nikým.' :
                      'Overací kód je citlivý údaj. NIKDY ho nezdieľaj s nikým, ani s kamarátmi. Podvodník by ho mohl použiť.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[7] === 'A' ? 'Overací kód je citlivý údaj. Ak ho zadáš do podozrivej stránky, podvodník ho získa a môže potvrdiť transakcie, zmeny na účte alebo prevody peňazí. Nikdy nezadávaj kód do stránky, ktorú si neoveril.' :
                      selectedAnswers[7] === 'B' ? 'Overací kód NIE JE len číslo. Je to citlivý údaj, ktorý potvrdzuje, že si to naozaj ty. Podvodník môže s tvojím kódom potvrdiť transakcie, prevody alebo zmeny na účte. Nikdy ho nezadávaj do podozrivej stránky.' :
                      selectedAnswers[7] === 'C' ? 'Overací kód je ako druhé heslo. Potvrdzuje, že si to naozaj ty. Ak ho zadáš do podozrivej stránky, podvodník ho získa a môže potvrdiť transakcie, prevody alebo zmeny na tvojom účte. Nikdy nezadávaj kód do stránky, ktorú si neoveril.' :
                      'Overací kód je citlivý údaj. NIKDY ho nezdieľaj s nikým, ani s kamarátmi. Podvodník by ho mohl použiť na potvrdenie transakcií alebo zmien na tvojom účte.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 8: Social pressure */}
          {currentStep === 8 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">👥</div>
                  <h2 className="text-2xl font-bold text-gray-900">Tlak od kamaráta</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Sára sa zverí kamarátovi, ktorý jej povie: <em>"Musíš to urobiť rýchlo, kým ti nezablokujú účet! Ja by som to urobil hneď."</em>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Sára cíti tlak, aby konala okamžite.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo by si poradil Sáre?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(8, 'A')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Kamarát má pravdu, urob to hneď</div>
                  <div className="text-sm text-gray-600">Kamarát vie, čo je správne</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(8, 'B')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Keď ti niekto radí, aby si to urobil rýchlo, je to dôkaz, že je to dôležité</div>
                  <div className="text-sm text-gray-600">Viacerí ľudia = pravda</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(8, 'C')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Tlak od iných by nemal nahradiť vlastné overenie. Vždy over najprv</div>
                  <div className="text-sm text-gray-600">Bezpečnosť je dôležitejšia ako rýchlosť</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(8, 'D')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Nechaj kamaráta, nech to urobí za teba</div>
                  <div className="text-sm text-gray-600">Kamarát to vyrieši</div>
                </button>
              </div>

              {selectedAnswers[8] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[8] === 'C' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[8] === 'C' ? '✅ Správne!' : '⚠️ Pozor!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[8] === 'A' ? 'Kamarát možno myslí dobre, ale nemusí vedieť o bezpečnosti. Lepšie je overiť cez oficiálnu aplikáciu alebo kontakt.' :
                      selectedAnswers[8] === 'B' ? 'Tlak na rýchle konanie je varovný signál, nie dôkaz dôležitosti. Vždy si over situáciu sám.' :
                      selectedAnswers[8] === 'C' ? 'Presne tak! Tlak od iných by nemal nahradiť vlastné overenie. Aj keď kamarát myslí dobre, bezpečnosť je dôležitejšia ako rýchlosť.' :
                      'NIKDY nedávaj nikomu prístup k svojmu účtu. Aj keď je to kamarát, je to veľké riziko.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[8] === 'A' ? 'Kamarát možno myslí dobre, ale nemusí vedieť o bezpečnosti. Lepšie je overiť situáciu cez oficiálnu aplikáciu, webstránku alebo telefónnu linku banky, než riskovať kvôli rade od niekoho iného.' :
                      selectedAnswers[8] === 'B' ? 'Tlak na rýchle konanie je varovný signál, nie dôkaz dôležitosti. Podvodníci používajú tlak, aby ťa donútili konať bez premyslenia. Vždy si over situáciu sám cez oficiálne kanály.' :
                      selectedAnswers[8] === 'C' ? 'Tlak od iných (aj od kamarátov) by nemal nahradiť vlastné overenie. Kamarát možno myslí dobre, ale bezpečnosť je dôležitejšia ako rýchlosť. Vždy si over situáciu cez oficiálnu aplikáciu alebo kontakt.' :
                      'NIKDY nedávaj nikomu prístup k svojmu účtu, ani kamarátovi. Aj keď myslí dobre, je to veľké riziko – mohol by omylom urobiť chybu alebo by niekto iný mohl získať prístup.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 9: Safe verification */}
          {currentStep === 9 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">✅</div>
                  <h2 className="text-2xl font-bold text-gray-900">Ako overiť bezpečne?</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Sára sa rozhodne overiť, či je správa pravá.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Aké sú bezpečné spôsoby overenia?
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Ktorý spôsob je najbezpečnejší?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(9, 'A')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Klikni na link v SMS a over tam</div>
                  <div className="text-sm text-gray-600">Link v správe je najrýchlejší</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(9, 'B')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Over cez oficiálnu bankovú aplikáciu, oficiálnu webstránku alebo zavolaj na oficiálnu linku banky</div>
                  <div className="text-sm text-gray-600">Oficiálne kanály sú bezpečné</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(9, 'C')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Opýtaj sa na sociálnych sieťach, či niekto dostal podobnú správu</div>
                  <div className="text-sm text-gray-600">Ostatní ti poradia</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(9, 'D')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Nič nerob, správu ignoruj úplne</div>
                  <div className="text-sm text-gray-600">Bezpečnosť = nič nerobiť</div>
                </button>
              </div>

              {selectedAnswers[9] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[9] === 'B' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[9] === 'B' ? '✅ Výborne!' : '⚠️ Nie celkom!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[9] === 'A' ? 'Kliknutie na link v podozrivej SMS je riskantné. Bezpečnejšie je overiť cez oficiálnu aplikáciu alebo kontakt.' :
                      selectedAnswers[9] === 'B' ? 'Presne tak! Najbezpečnejšie je overiť cez oficiálnu bankovú aplikáciu, oficiálnu webstránku (zadanú sám, nie cez link) alebo zavolať na oficiálnu telefónnu linku banky.' :
                      selectedAnswers[9] === 'C' ? 'Sociálne siete nie sú spoľahlivý zdroj overenia. Lepšie je overiť cez oficiálnu aplikáciu alebo telefónnu linku banky.' :
                      'Ignorovať správu úplne nie je vždy múdre – mohla by byť legitímna. Lepšie je overiť cez oficiálnu aplikáciu alebo telefónnu linku.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[9] === 'A' ? 'Kliknutie na link v podozrivej SMS je veľmi riskantné. Bezpečnejšie je overiť situáciu cez oficiálnu bankovú aplikáciu, oficiálnu webstránku (zadanú sám, nie cez link) alebo zavolať na oficiálnu telefónnu linku banky.' :
                      selectedAnswers[9] === 'B' ? 'Najbezpečnejšie je overiť cez oficiálne kanály: bankovú aplikáciu, oficiálnu webstránku (zadanú sám, nie cez link), oficiálnu telefónnu linku banky alebo dôveryhodnú dospelú osobu. Tak sa vyhnete riziku podvodu.' :
                      selectedAnswers[9] === 'C' ? 'Sociálne siete nie sú spoľahlivý zdroj overenia. Lepšie je overiť cez oficiálnu bankovú aplikáciu, oficiálnu webstránku alebo zavolať na oficiálnu telefónnu linku banky.' :
                      'Ignorovať správu úplne nie je vždy múdre – mohla by byť legitímna. Lepšie je overiť situáciu cez oficiálnu bankovú aplikáciu, oficiálnu webstránku alebo telefónnu linku banky.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 10: Final decision with quality-based reflection */}
          {currentStep === 10 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🎯</div>
                  <h2 className="text-2xl font-bold text-gray-900">Finálne rozhodnutie</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Sára má rozhodnúť: Čo je najbezpečnejšie urobiť?
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    <li>Dostala naliehavú SMS</li>
                    <li>Link vyzerá podozrivo</li>
                    <li>Stránka žiada heslo a overací kód</li>
                    <li>Kamarát ju tlačí, aby to urobila rýchlo</li>
                  </ul>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo je najbezpečnejšie rozhodnutie?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(10, 'A')
                    // Stay on step 10 to show reflection
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Urob to rýchlo cez link, kým účet nezablokujú</div>
                  <div className="text-sm text-gray-600">Urgentnosť je dôležitá</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(10, 'B')
                    // Stay on step 10 to show reflection
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) IGNORUJ podozrivú správu. Over situáciu cez oficiálnu aplikáciu, webstránku alebo telefónnu linku</div>
                  <div className="text-sm text-gray-600">Bezpečné overenie je kľúčové</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(10, 'C')
                    // Stay on step 10 to show reflection
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Pošli heslo a overací kód kamarátovi, nech to vyrieši za teba</div>
                  <div className="text-sm text-gray-600">Kamarát pomôže</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(10, 'D')
                    // Stay on step 10 to show reflection
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Klikni na link a zadaj len heslo, ale nie overací kód</div>
                  <div className="text-sm text-gray-600">Heslo nie je tak citlivé</div>
                </button>
              </div>

              {selectedAnswers[10] && (
                <div>
                  <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                    selectedAnswers[10] === 'B' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                  }`}>
                    <p className="font-semibold text-gray-800 mb-2">
                      {selectedAnswers[10] === 'B' ? '✅ Výborne!' : '⚠️ POZOR!'}
                    </p>
                    <p className="text-gray-700 text-sm mb-3">
                      <strong>Tip:</strong> {
                        selectedAnswers[10] === 'A' ? 'Urobiť to rýchlo cez podozrivý link je veľmi riskantné. Podvodník by získal tvoje údaje.' :
                        selectedAnswers[10] === 'B' ? 'Presne tak! Ignoruj podozrivú správu a over situáciu cez oficiálnu aplikáciu, webstránku (zadanú sám) alebo telefónnu linku banky. Tak si chránený.' :
                        selectedAnswers[10] === 'C' ? 'NIKDY nezdieľaj heslo ani overací kód s nikým, ani s kamarátom. To je veľmi nebezpečné.' :
                        'Zadať heslo cez podozrivý link je riskantné, aj keď nezadáš kód. Podvodník získa tvoje heslo.'
                      }
                    </p>
                    <p className="text-gray-700 text-sm">
                      <strong>Vysvetlenie:</strong> {
                        selectedAnswers[10] === 'A' ? 'Urobiť to rýchlo cez podozrivý link by znamenalo, že podvodník získa tvoje heslo a overací kód. Mohol by ti vykradnúť účet, previesť peniaze alebo urobiť iné škody. Vždy over cez oficiálne kanály.' :
                        selectedAnswers[10] === 'B' ? 'Ignoruj podozrivú správu a over situáciu cez oficiálnu bankovú aplikáciu, webstránku (zadanú sám, nie cez link) alebo zavolaj na oficiálnu telefónnu linku banky. Tak sa vyhnete riziku podvodu a ochránite svoj účet.' :
                        selectedAnswers[10] === 'C' ? 'NIKDY nezdieľaj heslo ani overací kód s nikým, ani s kamarátom. To je veľmi nebezpečné – podvodník by mohol získať prístup k tvojmu účtu, previesť peniaze alebo urobiť iné škody.' :
                        'Zadať heslo cez podozrivý link je riskantné, aj keď nezadáš kód. Podvodník získa tvoje heslo a môže sa pokúsiť získať aj kód iným spôsobom. Vždy over cez oficiálne kanály.'
                      }
                    </p>
                  </div>

                  {/* Quality-based final reflection */}
                  <div className="mt-8 p-8 bg-gradient-to-br from-red-50 to-orange-50 rounded-xl border-2 border-red-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <span className="text-2xl">📝</span>
                      Tvoje rozhodovanie
                    </h3>
                    
                    {(() => {
                      const score = getDecisionQuality()
                      
                      if (score >= 6) {
                        return (
                          <div className="space-y-3">
                            <p className="text-green-700 font-semibold">
                              ✅ Výborne! Rozhodoval si veľmi bezpečne.
                            </p>
                            <p className="text-gray-700">
                              Pochopil si, že <strong>naliehavosť je varovný signál</strong>, nie dôkaz, že správa je pravá. Uvedomil si, že <strong>aj malý rozdiel v linku môže znamenať podvod</strong> a že <strong>heslo a overací kód sú citlivé údaje</strong>.
                            </p>
                            <p className="text-gray-700">
                              Dokázal si rozpoznať, že <strong>tlak od iných by nemal nahradiť vlastné overenie</strong> a že <strong>najbezpečnejšie je overiť cez oficiálne kanály</strong> – aplikáciu, webstránku alebo telefónnu linku banky.
                            </p>
                            <p className="text-gray-700 font-medium">
                              💡 <strong>Záver:</strong> Bezpečné správanie znamená spomaliť, skontrolovať varovné signály a overiť predtým, ako konáš. Tak si chránený pred podvodmi.
                            </p>
                          </div>
                        )
                      } else if (score >= 4) {
                        return (
                          <div className="space-y-3">
                            <p className="text-yellow-700 font-semibold">
                              ⚠️ Niektoré rozhodnutia boli dobré, iné riskantné.
                            </p>
                            <p className="text-gray-700">
                              Niekedy si si uvedomil, že <strong>naliehavosť a podozrivé odkazy sú varovné signály</strong>. To je dobré!
                            </p>
                            <p className="text-gray-700">
                              Ale niekoľkokrát si <strong>ignoroval varovné znaky</strong> – možno si klikol na podozrivý link, zadal citlivé údaje alebo sa nechal ovplyvniť tlakom. To je riskantné a v reálnom živote by mohlo viesť k vykradnutiu účtu.
                            </p>
                            <p className="text-gray-700 font-medium">
                              💡 <strong>Čo si zapamätať:</strong> Naliehavosť, podozrivé odkazy a žiadosti o heslá sú varovné signály. Vždy over cez oficiálnu aplikáciu, webstránku alebo telefónnu linku banky. Tlak od iných by nemal nahradiť vlastné overenie.
                            </p>
                          </div>
                        )
                      } else {
                        return (
                          <div className="space-y-3">
                            <p className="text-red-700 font-semibold">
                              ⚠️ Tvoje rozhodovanie ignorovalo kritické varovné signály.
                            </p>
                            <p className="text-gray-700">
                              Niekoľkokrát si <strong>klikol na podozrivé odkazy, zadal citlivé údaje alebo sa nechal ovplyvniť tlakom</strong>. To je veľmi riskantné a v reálnom živote by to mohlo viesť k vykradnutiu účtu, strate peňazí alebo iným škodám.
                            </p>
                            <p className="text-gray-700">
                              <strong>Ignoroval si varovné signály</strong> – naliehavosť, podozrivé odkazy, žiadosti o heslá a kódy. Podvodníci používajú presne tieto triky, aby ťa oklamali.
                            </p>
                            <p className="text-gray-700 font-medium">
                              💡 <strong>Čo by si mal vedieť:</strong> Naliehavosť je varovný signál, nie dôkaz dôležitosti. Podozrivé odkazy a žiadosti o heslá sú znaky podvodu. Heslo a overací kód sú citlivé údaje – NIKDY ich nezadávaj do podozrivej stránky ani nezdieľaj s nikým.
                            </p>
                            <p className="text-gray-700">
                              <strong>Zapamätaj si:</strong> Vždy over cez oficiálnu bankovú aplikáciu, webstránku (zadanú sám, nie cez link) alebo zavolaj na oficiálnu telefónnu linku banky. Ak si neistý, poraď sa s dôveryhodnou dospelou osobou. Nikdy neklikaj na podozrivé odkazy a nikdy nezadávaj citlivé údaje do stránky, ktorú si neoveril.
                            </p>
                          </div>
                        )
                      }
                    })()}
                  </div>

                  <div className="mt-8 flex justify-center gap-4">
                    <button
                      onClick={() => navigate('/scenarios/8')}
                      className="px-6 py-3 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition"
                    >
                      Späť na scenáre
                    </button>
                    <button
                      onClick={() => {
                        setCurrentStep(1)
                        setSelectedAnswers({})
                      }}
                      className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-bold hover:bg-gray-300 transition"
                    >
                      Skúsiť znova
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

    </div>
  )
}

export default SuspiciousBankMessageScenario
